/**
 * Kizen contracting-request pipeline schema (Pinnacle FMO).
 *
 * Kizen custom objects store fields keyed by field-definition UUIDs. Each field
 * object in record payloads carries its own machine name (`name`), UI label
 * (`display_name`), and `field_type`, which determines the shape of `value`.
 *
 * Field metadata source of truth:
 * GET /api/records/{objectId}/fields
 *
 * Record data source:
 * POST /api/records/{objectId}/search
 */
export { KIZEN_CONTRACTING_REQUEST_FIELD_BY_NAME, } from "./kizen-field-registry.js";
import { KIZEN_CONTRACTING_REQUEST_FIELD_BY_NAME, } from "./kizen-field-registry.js";
export const KIZEN_CONTRACTING_REQUEST_OBJECT_ID = "015c7540-97ae-4a2b-976e-84d08905bf9b";
/** Curated pipeline-table columns — friendly keys mapped to Kizen `name`s. */
export const KIZEN_CONTRACTING_REQUEST_FIELDS = {
    stage: KIZEN_CONTRACTING_REQUEST_FIELD_BY_NAME.stage,
    name: KIZEN_CONTRACTING_REQUEST_FIELD_BY_NAME.name,
    displayName: KIZEN_CONTRACTING_REQUEST_FIELD_BY_NAME.display_name,
    typeOfRequest: KIZEN_CONTRACTING_REQUEST_FIELD_BY_NAME.type_of_request,
    requestedCarrierContract: KIZEN_CONTRACTING_REQUEST_FIELD_BY_NAME.requested_carrier_contract_unique_id,
    contractingRequestStatus: KIZEN_CONTRACTING_REQUEST_FIELD_BY_NAME.contracting_request_status,
    missingInformation: KIZEN_CONTRACTING_REQUEST_FIELD_BY_NAME.missing_information,
    requestingAgentAgency: KIZEN_CONTRACTING_REQUEST_FIELD_BY_NAME.requesting_agentagency,
    agentLevel: KIZEN_CONTRACTING_REQUEST_FIELD_BY_NAME.agent_level,
    requestedDirectUpline: KIZEN_CONTRACTING_REQUEST_FIELD_BY_NAME.direct_upline,
    requestedDirectUplineNpn: KIZEN_CONTRACTING_REQUEST_FIELD_BY_NAME.direct_upline_npn,
    requestedDirectUplineWritingId: KIZEN_CONTRACTING_REQUEST_FIELD_BY_NAME.direct_upline_writing_id,
    validDirectUplineForCarrier: KIZEN_CONTRACTING_REQUEST_FIELD_BY_NAME.valid_direct_upline_for_carrier,
    validDirectUplineForCarrierNpn: KIZEN_CONTRACTING_REQUEST_FIELD_BY_NAME.valid_direct_upline_for_carrier_npn,
    currentDirectUpline: KIZEN_CONTRACTING_REQUEST_FIELD_BY_NAME.current_direct_upline,
};
/** Field UUIDs to pass in search `field_ids` — order matches Kizen UI columns. */
export const KIZEN_CONTRACTING_REQUEST_FIELD_IDS = [
    KIZEN_CONTRACTING_REQUEST_FIELDS.name.id,
    KIZEN_CONTRACTING_REQUEST_FIELDS.typeOfRequest.id,
    KIZEN_CONTRACTING_REQUEST_FIELDS.requestedCarrierContract.id,
    KIZEN_CONTRACTING_REQUEST_FIELDS.stage.id,
    KIZEN_CONTRACTING_REQUEST_FIELDS.contractingRequestStatus.id,
    KIZEN_CONTRACTING_REQUEST_FIELDS.missingInformation.id,
    KIZEN_CONTRACTING_REQUEST_FIELDS.requestingAgentAgency.id,
    KIZEN_CONTRACTING_REQUEST_FIELDS.agentLevel.id,
    KIZEN_CONTRACTING_REQUEST_FIELDS.displayName.id,
    KIZEN_CONTRACTING_REQUEST_FIELDS.requestedDirectUpline.id,
    KIZEN_CONTRACTING_REQUEST_FIELDS.requestedDirectUplineNpn.id,
    KIZEN_CONTRACTING_REQUEST_FIELDS.requestedDirectUplineWritingId.id,
    KIZEN_CONTRACTING_REQUEST_FIELDS.validDirectUplineForCarrier.id,
    KIZEN_CONTRACTING_REQUEST_FIELDS.validDirectUplineForCarrierNpn.id,
    KIZEN_CONTRACTING_REQUEST_FIELDS.currentDirectUpline.id,
];
/** Stage dropdown option name → pipeline outcome from `/fields` options[].status. */
export const KIZEN_STAGE_OUTCOME_BY_NAME = {
    "New Request": "open",
    "Request Cannot be Processed": "open",
    "Link Sent": "open",
    Processing: "open",
    Certifications: "open",
    Active: "won",
    "Carrier Denied": "lost",
    Canceled: "lost",
    "Duplicate Requests": "disqualified",
    "Pending Request - Working": "open",
    "Pending Request - On Hold": "open",
    "Closed - Canceled": "lost",
    "Closed - Active": "won",
};
function asRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value)
        ? value
        : {};
}
function asString(value) {
    if (typeof value === "string" && value.trim())
        return value.trim();
    if (typeof value === "number" && Number.isFinite(value))
        return String(value);
    return undefined;
}
/** Index a Kizen `fields` map by each field's machine `name` (snake_case). */
export function indexKizenFieldsByName(fields) {
    const byName = {};
    if (!fields)
        return byName;
    for (const field of Object.values(fields)) {
        const key = field.name?.trim();
        if (key)
            byName[key] = field;
    }
    return byName;
}
function readNestedLabel(value) {
    if (typeof value === "string" || typeof value === "number")
        return asString(value);
    const record = asRecord(value);
    return asString(record.display_name) ?? asString(record.name) ?? asString(record.status);
}
/** Read a display string from a field, regardless of field_type. */
export function readKizenFieldLabel(field) {
    if (!field)
        return undefined;
    return readNestedLabel(field.value);
}
/** Read tag names from a dynamictags field. */
export function readKizenTagNames(field) {
    if (!field || !Array.isArray(field.value))
        return [];
    return field.value
        .map((entry) => asString(asRecord(entry).name))
        .filter((entry) => Boolean(entry));
}
/** Read a relationship field as id + human labels. */
export function readKizenRelationship(field) {
    const rawValue = Array.isArray(field?.value) ? field?.value[0] : field?.value;
    const value = asRecord(rawValue);
    const id = asString(value.id);
    const displayName = asString(value.display_name);
    const name = asString(value.name);
    const label = displayName ?? name ?? id;
    return {
        id,
        label: label ?? "",
        number: name,
        displayName,
    };
}
/** Read stage pipeline outcome (`open`/`won`/`lost`) from a stage field value. */
export function readKizenStageOutcome(field) {
    const value = asRecord(field?.value);
    const directStatus = asString(value.status);
    if (directStatus === "open" ||
        directStatus === "won" ||
        directStatus === "lost" ||
        directStatus === "disqualified") {
        return directStatus;
    }
    const stageName = asString(value.name);
    return stageName ? KIZEN_STAGE_OUTCOME_BY_NAME[stageName] : undefined;
}
/** Lookup a field by curated schema key (UUID internally, resolves via fields map). */
export function getKizenField(fields, key) {
    if (!fields)
        return undefined;
    const definition = KIZEN_CONTRACTING_REQUEST_FIELDS[key];
    return fields[definition.id] ?? indexKizenFieldsByName(fields)[definition.name];
}
/** Lookup a field by Kizen machine name from the `/fields` registry. */
export function getKizenFieldByName(fields, name) {
    if (!fields)
        return undefined;
    const definition = KIZEN_CONTRACTING_REQUEST_FIELD_BY_NAME[name];
    return fields[definition.id] ?? indexKizenFieldsByName(fields)[definition.name];
}
/** Parse a pipeline record into the same columns Kizen shows in its table UI. */
export function parseKizenContractingRequestRecord(record) {
    const fields = record.fields ?? {};
    const requestName = readKizenFieldLabel(getKizenField(fields, "name")) ??
        readKizenFieldLabel(getKizenField(fields, "displayName")) ??
        record.id;
    const requestingAgent = readKizenRelationship(getKizenField(fields, "requestingAgentAgency"));
    const requestedDirectUpline = readKizenRelationship(getKizenField(fields, "requestedDirectUpline"));
    const validDirectUpline = readKizenRelationship(getKizenField(fields, "validDirectUplineForCarrier"));
    const currentDirectUpline = readKizenRelationship(getKizenField(fields, "currentDirectUpline"));
    const directUpline = requestedDirectUpline.id || requestedDirectUpline.label
        ? requestedDirectUpline
        : validDirectUpline.id || validDirectUpline.label
            ? validDirectUpline
            : currentDirectUpline;
    return {
        recordId: record.id,
        requestName,
        typeOfRequest: readKizenFieldLabel(getKizenField(fields, "typeOfRequest")) ?? "",
        carrier: readKizenFieldLabel(getKizenField(fields, "requestedCarrierContract")) ?? "",
        stage: readKizenFieldLabel(getKizenField(fields, "stage")) ?? "",
        contractingRequestStatus: readKizenFieldLabel(getKizenField(fields, "contractingRequestStatus")) ?? "",
        missingInformation: readKizenTagNames(getKizenField(fields, "missingInformation")),
        requestingAgentAgency: {
            id: requestingAgent.id,
            label: requestingAgent.displayName && requestingAgent.number
                ? `${requestingAgent.displayName} - ${requestingAgent.number}`
                : requestingAgent.label,
            number: requestingAgent.number,
        },
        directUpline: {
            id: directUpline.id,
            label: directUpline.label,
            npn: readKizenFieldLabel(getKizenField(fields, "requestedDirectUplineNpn")) ??
                readKizenFieldLabel(getKizenField(fields, "validDirectUplineForCarrierNpn")),
            writingId: readKizenFieldLabel(getKizenField(fields, "requestedDirectUplineWritingId")),
        },
        agentLevel: readKizenFieldLabel(getKizenField(fields, "agentLevel")) ?? "",
    };
}
